import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./GoodGovernance.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---------------- Inline icon set (no external icon package needed) ---------------- */

const IconOrgGroup = ({ color = "#ffffff" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="12" r="4.5" stroke={color} strokeWidth="2" />
    <circle cx="12" cy="30" r="4" stroke={color} strokeWidth="2" />
    <circle cx="36" cy="30" r="4" stroke={color} strokeWidth="2" />
    <path d="M24 16.5v6M18 26l3-3M30 26l-3-3" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 34.5V38M36 34.5V38M8 42h8M32 42h8" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M20 40h8" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconFlagGrowth = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 42V10" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 12h16l-3 5 3 5H8" stroke="#1a6b3c" strokeWidth="2" strokeLinejoin="round" />
    <path d="M16 42l8-8 6 5 10-11" stroke="#ef7f3a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M34 28h6v6" stroke="#ef7f3a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconBank = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 18l18-10 18 10" stroke="#1a6b3c" strokeWidth="2" strokeLinejoin="round" />
    <path d="M8 18h32M10 18v18M18 18v18M30 18v18M38 18v18" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M6 42h36" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 36h36" stroke="#1a6b3c" strokeWidth="2" />
  </svg>
);

const IconCoinHand = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="14" r="7" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M24 10v8M21 12.5h5.5a1.8 1.8 0 010 3.6H21" stroke="#1a6b3c" strokeWidth="1.6" strokeLinecap="round" />
    <path
      d="M6 30c4-3 8-3 11-1l7 4c1.6 1 1.4 3.4-.5 3.9l-8 2-6-1.5"
      stroke="#1a6b3c"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M6 28v14M42 28v10" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconPeopleGreen = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="18" cy="16" r="5" stroke="#ffffff" strokeWidth="2" />
    <circle cx="32" cy="16" r="5" stroke="#ffffff" strokeWidth="2" />
    <path d="M8 36c0-6.5 4.5-10.5 10-10.5s10 4 10 10.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
    <path d="M22 26c1.4-.7 3-1 4.5-1 5.5 0 10 4 10 10.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconHandsHeart = ({ color = "#ffffff" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 26c-3-2.8-5.2-5.4-5.2-8.5a4.5 4.5 0 019-1 4.5 4.5 0 019 1c0 3.1-2.2 5.7-5.2 8.5l-3.8 3.6-3.8-3.6z"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M9 30c-2 2.2-3.5 4.8-3.5 8 0 4 3.3 7 7.3 7" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M39 30c2 2.2 3.5 4.8 3.5 8 0 4-3.3 7-7.3 7" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.3 2 4.5 5.6 4c2.1-.3 4 .8 6.4 3 2.4-2.2 4.3-3.3 6.4-3 3.6.5 5.2 4.3 3.6 7.7C19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2.8 20c.7-3.6 3.3-5.6 6.2-5.6s5.5 2 6.2 5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M15.5 5.4a3.2 3.2 0 010 6.2M18.8 20c-.5-2.6-1.9-4.4-3.8-5.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconHandHeart = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 38c-4-4-7-8-7-13a7 7 0 0114-1 7 7 0 0114 1c0 5-3 9-7 13l-7 7-7-7z"
      stroke="#ffffff"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 34c-3 3-6 7-6 12 0 5 4 9 9 9h9c4 0 7-2 10-5l9-9c1.5-1.5 1.5-3.7 0-5-1.4-1.3-3.4-1.2-4.8.1l-6.2 6"
      fill="#ef7f3a"
    />
  </svg>
);

/* ------------------------------------------------------------------------------------ */

const GoodGovernance = () => {
  const initiatives = [
    {
      title: "Capacity Building",
      text: "Capacity building programs for tribes, women, and PwDs to enhance skills and confidence.",
      icon: <IconOrgGroup color="#1a6b3c" />,
    },
    {
      title: "Rural Finance Institutions",
      text: "Facilitating the formation of rural finance institutions to promote financial independence.",
      icon: <IconBank />,
    },
    {
      title: "Credit & Market Linkages",
      text: "Arranging credit facilities to create market linkages and improve livelihood opportunities.",
      icon: <IconCoinHand />,
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="gg-hero">
        <div
          className="gg-hero__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Good.png)",
          }}
        ></div>
        <div className="gg-hero__overlay"></div>
        <div className="gg-hero__dots"></div>

        <div className="gg-hero__logo">
          <IconHandHeart />
        </div>

        <svg className="gg-hero__wave" viewBox="0 0 200 260" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2 2c30 20-20 45 10 65s45-10 60 15-10 40 20 60"
            stroke="#ef7f3a"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>

        <Container className="gg-hero__content">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Good
            <br />
            <span>Governance</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Vikash promotes good governance by fostering transparency,
            accountability, and active community participation in
            decision-making processes.
          </motion.p>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="gg-intro">
        <div className="gg-intro__dots"></div>
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="gg-icon-circle">
                  <IconOrgGroup />
                </div>
                <div className="gg-eyebrow">Good Governance</div>
                <span className="gg-eyebrow__dash"></span>
                <h2 className="gg-heading">
                  Empowering Communities
                  <br />
                  for a <span>Better Future</span>
                </h2>
                <p className="gg-text">
                  Vikash organizes capacity building programs for
                  indigenous tribes, women, and other disadvantaged
                  communities to improve their socio-economic conditions.
                  We facilitate the formation of rural finance
                  institutions, arrange credit, and create market
                  linkages, advocating for diverse livelihood options to
                  enhance income levels.
                </p>

                <div className="gg-badge">
                  <div className="gg-badge__icon">
                    <IconPeopleGreen />
                  </div>
                  <div>
                    <strong>Stronger communities. Better tomorrow.</strong>
                    <p>
                      Through transparent governance and active
                      participation, we build communities that are
                      empowered and self-reliant.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="gg-intro__imgwrap">
                  <div className="gg-intro__accent"></div>
                  <img
                    src="/assets/images/sections/Gg1.jpg"
                    alt="Community meeting on good governance"
                    className="gg-intro__img"
                  />
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Our Core Strategies Section */}
      <section className="gg-strategies">
        <div className="gg-strategies__dots"></div>
        <div className="gg-strategies__leaf">
          <svg viewBox="0 0 120 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M60 150V40" stroke="#cddccf" strokeWidth="3" />
            <path d="M60 60c-15-15-35-15-45-5 10 15 30 15 45 5z" fill="#cddccf" />
            <path d="M60 90c15-15 35-15 45-5-10 15-30 15-45 5z" fill="#cddccf" />
            <path d="M60 120c-12-12-28-12-36-4 8 12 24 12 36 4z" fill="#cddccf" />
          </svg>
        </div>

        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center gg-strategies__head"
          >
            <div className="gg-strategies__icon">
              <IconFlagGrowth />
            </div>
            <h2 className="gg-heading gg-heading--center">
              Our Core Strategies for
              <br />
              <span>Good Governance</span>
            </h2>
            <span className="gg-underline gg-underline--center"></span>
            <p className="gg-subtext">
              We focus on building skills and financial independence to
              foster strong communities.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((item, index) => (
                <Col md={6} lg={5} key={index}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="gg-card h-100">
                      <Card.Body className="d-flex align-items-start">
                        <div className="gg-card__icon">{item.icon}</div>
                        <div>
                          <Card.Title as="h5">{item.title}</Card.Title>
                          <span className="gg-card__underline"></span>
                          <Card.Text>{item.text}</Card.Text>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="gg-cta">
        <Container>
          <motion.div
            className="gg-cta__panel"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="gg-cta__dots"></div>
            <Row className="align-items-center g-0">
              <Col lg={6} className="gg-cta__left">
                <div className="gg-cta__icon">
                  <IconHandsHeart color="#ef7f3a" />
                </div>
                <h2 className="gg-cta__title">
                  Support Transparent
                  <br />
                  <span>Communities</span>
                </h2>
                <span className="gg-underline"></span>
                <p className="gg-cta__text">
                  Your contribution helps us provide training and
                  resources that empower communities to build a better,
                  more accountable future.
                </p>
                <div className="gg-cta__actions">
                  <Button className="gg-btn gg-btn--solid" onClick={openDonation}>
                    <IconHeart /> Donate Now
                  </Button>
                  <Button
                    className="gg-btn gg-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUsers /> Join Us
                  </Button>
                </div>
              </Col>

              <Col lg={6} className="gg-cta__right">
                <img
                  src="/assets/images/backgrounds/do4.png"
                  alt="Community members gathered in discussion"
                  className="gg-cta__img"
                />
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default GoodGovernance;